import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * CollectionSEO - Dynamic SEO metadata and JSON-LD structured data for Collection/Category pages.
 * Conforms to Schema.org standards and uses actual route/category data.
 */
function CollectionSEO({ gender, subcategoryName, categoryImage, products, loading, error }) {
  if (loading) {
    return (
      <Helmet>
        <title>Loading Collection... | DOR Dress On Rent</title>
      </Helmet>
    );
  }

  if (error || !gender || !subcategoryName || (gender !== 'men' && gender !== 'women')) {
    return (
      <Helmet>
        <title>Collection Not Found | DOR Dress On Rent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
    );
  }

  const cleanSubcategory = subcategoryName.trim();
  const genderLabel = gender === 'men' ? ' for Men' : gender === 'women' ? ' for Women' : '';
  const genderPhrase = gender === 'men' ? 'for men' : gender === 'women' ? 'for women' : '';
  const genderName = gender === 'men' ? 'Men' : gender === 'women' ? 'Women' : gender;

  // 1. Dynamic Title (~50-60 chars)
  const defaultSuffix = ' | DOR Dress On Rent';
  const shortSuffix = ' | DOR';
  const baseTitle = `${cleanSubcategory} on Rent${genderLabel}`;

  let seoTitle = baseTitle + defaultSuffix;
  if (seoTitle.length > 60) {
    if ((baseTitle + shortSuffix).length <= 60) {
      seoTitle = baseTitle + shortSuffix;
    } else {
      const maxLen = 60 - shortSuffix.length - ` on Rent${genderLabel}`.length;
      const truncated = cleanSubcategory.slice(0, Math.max(8, maxLen - 1)).trim();
      seoTitle = `${truncated}… on Rent${genderLabel}${shortSuffix}`;
    }
  }

  // 2. Dynamic Meta Description (~140-160 chars)
  const seoDescription = `Rent designer ${cleanSubcategory} outfits ${genderPhrase} from DOR Dress On Rent. Explore available rental styles and find the perfect look for your upcoming event.`;

  // 3. Canonical URL
  const encodedSubcategory = encodeURIComponent(cleanSubcategory);
  const canonicalUrl = `https://www.dordressonrent.com/collection/${gender}/${encodedSubcategory}`;

  // 4. Resolve Image (Category Image -> First Product Image -> Fallback Official Site OG Image)
  let ogImage = '';
  if (categoryImage && typeof categoryImage === 'string' && categoryImage.startsWith('http')) {
    ogImage = categoryImage;
  } else if (Array.isArray(products) && products.length > 0) {
    const firstProduct = products[0];
    const candidateImg =
      firstProduct?.thumbs?.sm?.url ||
      firstProduct?.thumbs?.xs?.url ||
      firstProduct?.imageUrl ||
      (Array.isArray(firstProduct?.images) && firstProduct.images[0]);

    if (candidateImg && typeof candidateImg === 'string' && candidateImg.startsWith('http')) {
      ogImage = candidateImg;
    }
  }

  if (!ogImage) {
    // Existing official website OG image from public/index.html
    ogImage = 'https://www.dordressonrent.com/og-image.jpg';
  }

  // 5. Schema.org CollectionPage Structured Data
  const collectionPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: seoTitle,
    description: seoDescription,
    url: canonicalUrl,
    isPartOf: {
      '@type': 'WebSite',
      name: 'DOR Dress On Rent',
      url: 'https://www.dordressonrent.com',
    },
  };

  // 6. Schema.org BreadcrumbList Structured Data
  // Using existing crawlable URLs only (no /#men, /#women, or /collection/:gender)
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.dordressonrent.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: genderName,
        item: 'https://www.dordressonrent.com/',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: cleanSubcategory,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Page Title & Description */}
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content="DOR Dress On Rent" />
      {ogImage && <meta property="og:image" content={ogImage} />}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured Data: CollectionPage */}
      <script type="application/ld+json">
        {JSON.stringify(collectionPageJsonLd)}
      </script>

      {/* Structured Data: BreadcrumbList */}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbJsonLd)}
      </script>
    </Helmet>
  );
}

export default CollectionSEO;
